import { MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { RunScope } from 'src/components/dispatch/autoDispatch/runIdentity';
import {
  AutoDispatchDeps,
  AutoDispatchHooks,
  useAutoDispatchController,
} from 'src/components/dispatch/autoDispatch/useAutoDispatchController';
import { UnassignedItem } from 'src/entities/dispatch';
import { DeliveryListGroupingValues } from 'src/entities/licenseEntity';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { RotationRow, VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';
import {
  resetOverrideForScopeChange,
  shouldClearOverrideAfterPlanningResult,
} from 'src/pages/ConvenienceDispatches/autoDispatchQueue';
import { resolveCompletionEffects } from 'src/pages/ConvenienceDispatches/resolveCompletionEffects';
import { shouldCancelForOverrideChange } from 'src/pages/ConvenienceDispatches/shouldCancelForOverrideChange';
import { debounce } from 'src/utils/debounce';

import { buildAssignmentTimelineInput } from './buildAssignmentTimelineInput';
import { BoardCommand } from './types';
import { AssignmentOverrideState, useAssignmentOverride } from './useAssignmentOverride';

/** D&D のあと自動配車を撃つまでの待ち時間。利用者が続けて操作する間は延長される */
export const AUTO_DISPATCH_DEBOUNCE_MS = 2000;

/** ドラッグ中の操作でも延長するイベント。pointer 系は dnd-kit が握るので DOM 側で拾う */
const IDLE_WATCH_EVENT_TYPES = [
  'mousedown', 'mousemove', 'mouseup', 'keydown', 'keyup', 'touchstart', 'touchmove', 'touchend',
];

/**
 * 自動配車の入力を組むために要る「表示中の盤面」。
 *
 * Why not 値で受ける: `rowGroups` は上書き（この hook が持つ）を被せたあとの行なので、
 * hook を呼ぶ時点ではまだ確定していない。`useAssignmentOverride` の `getRowLookup` と
 * 同じ理由で関数にする。読むのは debounce のタイマが発火したとき（= コミット後）だけ。
 */
export type DispatchAssignmentTimelineSource = {
  deliveries: PlanningsDeliveryWithLegRunEntity[];
  rowGroups: VehicleRowGroup[];
  rowIdToShiftTruckId: ReadonlyMap<string, number>;
  truckIdToDeliveryId: ReadonlyMap<number, number>;
};

/**
 * 画面固有の通知だけ。上書きの後始末（破棄・queued フラグ・停止時の復元）は
 * この hook が持つので、画面は「何を知らせるか」だけを書く。
 */
export type DispatchAssignmentNotifications = {
  onRunAccepted?: AutoDispatchHooks['onRunAccepted'];
  onStarted?: AutoDispatchHooks['onStarted'];
  onCompleted?: AutoDispatchHooks['onCompleted'];
  onScopeChanged?: AutoDispatchHooks['onScopeChanged'];
  onCancelled?: NonNullable<AutoDispatchHooks['onCancelled']>;
  onFailed?: AutoDispatchHooks['onFailed'];
};

export type DispatchAssignmentDeps = {
  scope: RunScope;
  getRowLookup: () => ReadonlyMap<string, RotationRow>;
  getTimelineSource: () => DispatchAssignmentTimelineSource;
  legRuns: ShipmentOrderLegRunForPlanEntity[];
  serverUnassigned: UnassignedItem[];
  deliveryListGrouping: DeliveryListGroupingValues;
  /** 移動したカード id。画面側のハイライトに使う */
  onMoved?: (cardIds: string[]) => void;
  /** 複数選択の解除など、画面固有の後処理 */
  onAfterApply?: (command: BoardCommand) => void;
  notifications?: DispatchAssignmentNotifications;
  /** controller のテスト用差し込み口（`useAutoDispatchController` の deps をそのまま通す） */
  controllerDeps?: Partial<AutoDispatchDeps>;
  /**
   * 自動配車以外で実行中か（手動リセットなど）。debounce の起動条件は
   * `controller.isBusy` だけでは足りないので画面から補う。
   */
  isExternallyBusy?: boolean;
};

export type DispatchAssignmentState = AssignmentOverrideState & {
  controller: ReturnType<typeof useAutoDispatchController>;
  /**
   * 実行開始時に未割当から消した一覧。停止したら戻すために覚える。
   * 画面は「送信中の案件の選択を落とさない」判定に読む。
   */
  unassignedBeforeRunRef: MutableRefObject<UnassignedItem[] | null>;
};

/**
 * D&D の 1 操作を楽観的に映し、少し待ってから自動配車（timeline）を撃つところまでを束ねる。
 *
 * ```
 * applyCommand → 楽観的上書き → 2000ms 待つ → buildAssignmentTimelineInput
 *   → controller.runTimeline → ポーリング → 完了 lifecycle で上書きを捨てる
 * ```
 *
 * `useAutoDispatchController` はこの hook の中で作る。controller は「完了したら上書きを
 * 捨てる」lifecycle（`AutoDispatchHooks`）を要り、その lifecycle は上書き state を読むので、
 * 外から controller を受け取る形にすると画面側で循環する。上書き → lifecycle → controller
 * → debounce の順に組めば依存は一方向になる。
 */
export function useDispatchAssignment(deps: DispatchAssignmentDeps): DispatchAssignmentState {
  const {
    scope, getRowLookup, getTimelineSource, legRuns, serverUnassigned, deliveryListGrouping,
    onMoved, onAfterApply, notifications, controllerDeps, isExternallyBusy = false,
  } = deps;

  // 1. controller を知らない上書き state
  const override = useAssignmentOverride({
    getRowLookup, legRuns, serverUnassigned, deliveryListGrouping, onMoved, onAfterApply,
  });
  const {
    overrideRowCards, overrideUnassigned, setOverrideRowCards, setOverrideUnassigned,
    hasQueuedOverrideChangeRef,
  } = override;

  // 自動配車に送る直前の未割当一覧。停止したときに消した案件を戻すために覚える
  const unassignedBeforeRunRef = useRef<UnassignedItem[] | null>(null);

  const clearLocalOverrides = useCallback(() => {
    setOverrideRowCards(new Map());
    setOverrideUnassigned(null);
  }, [setOverrideRowCards, setOverrideUnassigned]);

  // 2. 上書きの後始末 + 画面固有の通知。毎レンダー作り直してよい
  //    （controller は hooksRef 経由で最新を読む）
  const hooks: AutoDispatchHooks = {
    onRunAccepted: (context) => {
      // ここからは現在の上書きを新しい job の入力として扱う。
      // 以降に追加操作が入った場合は上書き監視 effect が再度 true にする。
      hasQueuedOverrideChangeRef.current = false;
      notifications?.onRunAccepted?.(context);
    },
    onStarted: (context) => {
      notifications?.onStarted?.(context);
      if (context.mode === 'normal' && context.orderIds.length > 0) {
        // 実行開始時点で未割当リストから対象案件を即時削除（V3Plans と同じオプティミスティック更新）。
        // polling 完了後にキャッシュが再取得され、画面側の effect で上書きがリセットされる。
        const removeIds = new Set(context.orderIds.map((id) => `lr-${id}`));
        setOverrideUnassigned((prev) => {
          const before = prev ?? serverUnassigned;
          // 停止したときに戻せるよう、消す前の一覧を覚えておく（上書き全体を捨てると
          // D&D で動かした並びまで戻ってしまう）
          unassignedBeforeRunRef.current = before;
          return before.filter((u) => !removeIds.has(u.id));
        });
      }
    },
    shouldClearLocalOverrides: () => shouldClearOverrideAfterPlanningResult(hasQueuedOverrideChangeRef.current),
    clearLocalOverrides,
    onCompleted: (result, context) => {
      const effects = resolveCompletionEffects(context.mode, context.completionKind);
      // 結果が出たので「止めたら戻す」ための控えは捨てる
      unassignedBeforeRunRef.current = null;
      if (effects.clearOverride) {
        hasQueuedOverrideChangeRef.current = false;
        clearLocalOverrides();
      }
      notifications?.onCompleted?.(result, context);
    },
    onScopeChanged: () => {
      resetOverrideForScopeChange({ setOverrideRowCards, setOverrideUnassigned, hasQueuedOverrideChangeRef });
      notifications?.onScopeChanged?.();
    },
    /**
     * 利用者が停止したとき。1 件も割り付いていないので、実行開始時に消した未割当を戻す。
     *
     * Why not clearLocalOverrides: cancel は上書きを保持する契約（D&D で動かした位置を
     * 残す）。未割当だけを消す前の状態へ戻す。
     */
    onCancelled: (context) => {
      const before = unassignedBeforeRunRef.current;
      unassignedBeforeRunRef.current = null;
      if (before) setOverrideUnassigned(before);
      notifications?.onCancelled?.(context);
    },
    onFailed: (error, context) => {
      hasQueuedOverrideChangeRef.current = false;
      unassignedBeforeRunRef.current = null;
      clearLocalOverrides();
      notifications?.onFailed?.(error, context);
    },
  };

  // 3. controller
  const controller = useAutoDispatchController({ scope, hooks, deps: controllerDeps });

  const isBusy = controller.isBusy || isExternallyBusy;

  // 4. debounce と実行
  const { startOn } = scope;
  const runAutoDispatch = useCallback(async () => {
    if (controller.isBusy) {
      console.log('[dispatch-assignment] runAutoDispatch skip: 実行中');
      return;
    }
    if (overrideRowCards.size === 0 && overrideUnassigned === null) {
      console.log('[dispatch-assignment] runAutoDispatch skip: 上書き空');
      return;
    }
    // hasQueuedOverrideChangeRef のリセットは controller.runTimeline 内の
    // onRunAccepted hook が行う（現在の上書きを新しい job の入力として扱う）
    console.log('[dispatch-assignment] runAutoDispatch start', {
      overrideRows: overrideRowCards.size,
      overrideUnassigned: overrideUnassigned?.length ?? null,
    });

    const source = getTimelineSource();
    const input = buildAssignmentTimelineInput({
      deliveries: source.deliveries,
      rowGroups: source.rowGroups,
      legRuns,
      serverUnassigned,
      overrideUnassigned,
      rowIdToShiftTruckId: source.rowIdToShiftTruckId,
      truckIdToDeliveryId: source.truckIdToDeliveryId,
      startOn,
    });
    if (input === null) {
      console.log('[dispatch-assignment] runAutoDispatch skip: 変更 legRun 無し');
      return;
    }

    await controller.runTimeline(input);
  }, [
    controller, overrideRowCards, overrideUnassigned, getTimelineSource, legRuns, serverUnassigned, startOn,
  ]);

  // 最新 runAutoDispatch を ref 経由で参照（debounce 関数から安定呼び出し）
  const runAutoDispatchRef = useRef(runAutoDispatch);
  useEffect(() => {
    runAutoDispatchRef.current = runAutoDispatch;
  }, [runAutoDispatch]);

  const debouncedAutoDispatch = useMemo(
    () => debounce(() => {
      console.log('[dispatch-assignment] debounce 発火 → runAutoDispatch 実行');
      void runAutoDispatchRef.current();
    }, AUTO_DISPATCH_DEBOUNCE_MS),
    [],
  );

  // 上書きの変更を検知。実行中の自動配車があれば即キャンセルして新規 debounce を開始する。
  // 「直前の job は無駄なので捨て、最新状態で再計算する」というポリシー (Q2-A)。
  // 初回マウント時の上書き = 空 では発火させない (prev=null check)
  const prevOverrideRef = useRef<{ r: typeof overrideRowCards; u: typeof overrideUnassigned } | null>(null);
  // controller.isBusy が「直前のレンダーでも既に true だったか」。cancel 判定
  // （shouldCancelForOverrideChange）が「受付と同一 commit」の自己キャンセルを
  // 避けるために使う。詳細は同ファイルの Why コメント参照
  const wasBusyRef = useRef(false);
  useEffect(() => {
    const prev = prevOverrideRef.current;
    const overrideChanged = prev !== null
      && (prev.r !== overrideRowCards || prev.u !== overrideUnassigned);
    prevOverrideRef.current = { r: overrideRowCards, u: overrideUnassigned };

    const wasBusy = wasBusyRef.current;
    wasBusyRef.current = controller.isBusy;

    if (shouldCancelForOverrideChange({ overrideChanged, isBusy: controller.isBusy, wasBusy })) {
      hasQueuedOverrideChangeRef.current = true;
      console.log('[dispatch-assignment] running 中に上書き変更 → queued + cancel');
      controller.cancel();
    }
  }, [overrideRowCards, overrideUnassigned, controller, hasQueuedOverrideChangeRef]);

  // dirty 状態になった瞬間に debounce を kick。以降のユーザー操作で延長される
  useEffect(() => {
    const isDirty = overrideRowCards.size > 0 || overrideUnassigned !== null;
    if (isDirty && !isBusy) {
      console.log('[dispatch-assignment] dirty 検知 → debounce 開始');
      debouncedAutoDispatch();
    } else {
      debouncedAutoDispatch.cancel();
    }
  }, [overrideRowCards, overrideUnassigned, isBusy, debouncedAutoDispatch]);

  // ユーザー操作中は debounce をリセットして自動発火を遅らせる
  useEffect(() => {
    const handler = () => {
      const isDirty = overrideRowCards.size > 0 || overrideUnassigned !== null;
      if (isDirty && !isBusy) {
        debouncedAutoDispatch();
      }
    };
    IDLE_WATCH_EVENT_TYPES.forEach((t) => document.addEventListener(t, handler, { passive: true }));
    return () => {
      IDLE_WATCH_EVENT_TYPES.forEach((t) => document.removeEventListener(t, handler));
    };
  }, [debouncedAutoDispatch, overrideRowCards, overrideUnassigned, isBusy]);

  // unmount 時にキャンセル
  useEffect(() => () => { debouncedAutoDispatch.cancel(); }, [debouncedAutoDispatch]);

  return { ...override, controller, unassignedBeforeRunRef };
}
